import axios from 'axios';
import { cacheManager } from '../utils/cacheManager';
import { trackEvent, resetPostHog } from '../utils/posthogProvider';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../utils/tokenUtils';

// Base URL - should be moved to environment variable
// Use HTTP for localhost (HTTPS requires SSL certificates which localhost typically doesn't have)
// const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api/v1/vendor";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor`;
export const BASE_API = `${process.env.REACT_APP_API_BASE_URL}`;

// Create axios instance with cache-control headers
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    // 'Cache-Control': 'no-cache, no-store, must-revalidate',
    // 'Pragma': 'no-cache',
    // 'Expires': '0'
  },
  credentials: "include"
});

// Request interceptor to add auth token
api.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('vendorAPI: Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ── Refresh-token state ──────────────────────────────────────────────────────
// Prevents multiple simultaneous refresh calls when concurrent requests 401.
let isRefreshing = false;
let pendingQueue = []; // [{ resolve, reject }]

const processPendingQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};
// ────────────────────────────────────────────────────────────────────────────

// Response interceptor — silent refresh on 401
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Never try to refresh for auth-related endpoints themselves
    const isAuthEndpoint =
      originalRequest?.url?.includes('/login') ||
      originalRequest?.url?.includes('/register') ||
      originalRequest?.url?.includes('/refresh-token');

    // Handle 401 with silent refresh
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue this request until the refresh resolves
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const storedRefreshToken = getRefreshToken();

      if (!storedRefreshToken) {
        // No refresh token at all — force login
        clearTokens();
        isRefreshing = false;
        processPendingQueue(error);
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await api.post('/refresh-token', {
          refresh_token: storedRefreshToken,
        });

        const newToken = refreshResponse.data?.token;
        if (!newToken) throw new Error('No token in refresh response');

        setTokens(newToken); // keep existing refresh_token unless server sends a new one
        processPendingQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh token is also invalid/expired — force login
        clearTokens();
        processPendingQueue(refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Limit Reached errors
    if (error.response?.status === 403) {
      const limitError = error.response?.data?.error;
      if (limitError && limitError.toLowerCase().includes('limit reached')) {
        // Dispatch custom event for global error handling (to show modal)
        const event = new CustomEvent('limit-reached', { detail: { message: limitError } });
        window.dispatchEvent(event);
      }
    }

    return Promise.reject(error);
  }
);

// ==================== Public Endpoints ====================

export const registerVendor = async data => {
  try {
    const response = await api.post('/register', {
      name: data.name || '',
      email: data.email,
      password: data.password,
      country: data.country,
    });

    // Store trial_end if returned from registration
    if (response.data?.trial_end) {
      localStorage.setItem('trial_end', response.data.trial_end);
    }

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const verifyRegistrationOTP = async (email, otp) => {
  try {
    const response = await api.post('/verify-registration-otp', { email, otp });

    // Track OTP verification success
    trackEvent('otp_verified', {
      email,
      type: 'registration',
    });

    return { success: true, data: response.data };
  } catch (error) {
    // Track OTP verification failure
    trackEvent('otp_verification_failed', {
      email,
      type: 'registration',
      error: error.response?.data?.error || error.message,
    });

    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const resendRegistrationOTP = async email => {
  try {
    const response = await api.post('/resend-registration-otp', { email });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const loginVendor = async (email, password) => {
  try {
    const response = await api.post('/login', { email, password });

    const { token, refresh_token, trial_end } = response.data;
    if (token) {
      // Store both access token (24 h) and refresh token (30 days)
      setTokens(token, refresh_token);
    }
    if (trial_end) {
      localStorage.setItem('trial_end', trial_end);
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Login error:', error);
    // Better error message extraction
    const errorMessage = error.response?.data?.error
      || error.response?.data?.message
      || error.message
      || 'Login failed. Please check your credentials and try again.';
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Manually refresh the access token using the stored refresh token.
 * Primarily called by the response interceptor; exported for testing.
 */
export const refreshAccessToken = async () => {
  const storedRefreshToken = getRefreshToken();
  if (!storedRefreshToken) return { success: false };
  try {
    const response = await api.post('/refresh-token', { refresh_token: storedRefreshToken });
    const { token } = response.data;
    if (token) setTokens(token);
    return { success: true, token };
  } catch (error) {
    clearTokens();
    return { success: false };
  }
};

export const forgotPassword = async email => {
  try {
    const response = await api.post('/forgot-password', { email });

    // Track password reset request
    trackEvent('password_reset_requested', {
      email,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const verifyOTP = async (email, otp) => {
  try {
    const response = await api.post('/verify-otp', { email, otp });

    // Track OTP verification for password reset
    trackEvent('otp_verified', {
      email,
      type: 'password_reset',
    });

    return { success: true, data: response.data };
  } catch (error) {
    // Track OTP verification failure
    trackEvent('otp_verification_failed', {
      email,
      type: 'password_reset',
      error: error.response?.data?.error || error.message,
    });

    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const resetPassword = async (email, newPassword) => {
  try {
    const response = await api.post('/reset-password', {
      email,
      password: newPassword,
    });

    // Track password reset completion
    trackEvent('password_reset_completed', {
      email,
    });

    // Reset PostHog session after password reset
    resetPostHog();

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getBankInfo = async () => {
  try {
    const response = await api.get('/bank_info');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateBankInfo = async (bankInfoId, bankInfoData) => {
  try {
    const response = await api.put(`/bank_info/${bankInfoId}`, bankInfoData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteBankInfo = async bankInfoId => {

  try {
    const response = await api.delete(`/bank_info/${bankInfoId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getServiceIcons = async () => {
  try {
    const response = await api.get('/service/icons');
    return {
      success: true,
      data: response.data?.service_icons ?? response.data?.data ?? [],
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/** Alias — service_icons[].id is sport_id */
export const getSports = getServiceIcons;

export const switchToCustomer = async () => {
  try {
    const response = await api.post('/switch-to-customer');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ==================== Authenticated Endpoints ====================

// Dashboard & Analytics
export const getDashboard = async () => {
  try {
    const response = await api.get('/dashboard');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getAnalytics = async (startDate, endDate) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/analytics', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getStaffAnalytics = async () => {
  try {
    const response = await api.get('/staff/analytics/');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Global cache for getAllSubscribedUsers to prevent excessive API calls
const subscribedUsersCache = {
  data: null,
  timestamp: null,
  TTL: 2 * 60 * 1000, // 2 minutes TTL (reduced from 5 minutes for fresher data)
  promise: null, // Track ongoing request to prevent duplicate calls
  clear: function () {
    this.data = null;
    this.timestamp = null;
    this.promise = null;
  }
};

// Register cache with cache manager
cacheManager.register('subscribedUsers', subscribedUsersCache);

export const getAllSubscribedUsers = async (forceRefresh = false) => {
  const now = Date.now();

  // Return cached data if still valid and not forcing refresh
  if (!forceRefresh && subscribedUsersCache.data && subscribedUsersCache.timestamp &&
    (now - subscribedUsersCache.timestamp) < subscribedUsersCache.TTL) {
    return { success: true, data: subscribedUsersCache.data };
  }

  // If there's an ongoing request, wait for it instead of making a new one
  if (subscribedUsersCache.promise) {
    try {
      return await subscribedUsersCache.promise;
    } catch (error) {
      // If the ongoing request fails, we'll make a new one
      subscribedUsersCache.promise = null;
    }
  }

  // Make the API call
  subscribedUsersCache.promise = (async () => {
    try {
      const response = await api.get('/staff/subscibed/');
      const result = { success: true, data: response.data };

      // Update cache
      subscribedUsersCache.data = response.data;
      subscribedUsersCache.timestamp = now;
      subscribedUsersCache.promise = null;

      return result;
    } catch (error) {
      subscribedUsersCache.promise = null;
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  })();

  return subscribedUsersCache.promise;
};

export const getFinanceStats = async () => {
  try {
    const response = await api.get('/finance/');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Subscription Management
export const confirmPlanPurchase = async subscriptionData => {
  try {
    const response = await api.post('/subscription/confirm', subscriptionData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Batches Management
export const getBatches = async () => {
  try {
    const response = await api.get('/batches');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getPriceSuggestions = async (serviceId) => {
  try {
    const response = await api.get(`/batches/price-suggestions?service_id=${serviceId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const previewBatchEarnings = async (price, currency = 'gbp') => {
  try {
    const response = await api.get(`/batches/preview-earnings?price=${price}&currency=${currency}`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getBatchById = async batchId => {
  try {
    const response = await api.get(`/batches/${batchId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createBatch = async batchData => {
  try {
    console.log(batchData, "Batch Data")
    const response = await api.post('/batches', batchData);

    // Track batch creation
    trackEvent('batch_created', {
      service_id: batchData.service_id,
      batch_name: batchData.batch_name,
      has_coach: !!batchData.coach_id,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ── Sportona AI Batch Draft ────────────────────────────────────────────────────
// Sends a natural-language prompt to the backend (pip.go → FunctionRegistry).
// Returns { Applied, Assumed, StillToSet } — never saves anything automatically.
export const generateBatchDraftAI = async (prompt) => {
  try {
    const response = await api.post('/pip/batch-draft', { prompt });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


export const updateBatch = async (batchId, batchData) => {
  try {
    const response = await api.put(`/batches/${batchId}`, batchData);

    // Track batch update
    trackEvent('batch_updated', {
      batch_id: batchId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Freeze Batch
export const freezeBatch = async batchId => {
  try {
    const response = await api.patch(`/batches/${batchId}`);

    // Track batch freeze/unfreeze (API toggles status)
    trackEvent('batch_status_toggled', {
      batch_id: batchId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const unfreezeBatch = async batchId => {
  try {
    const response = await api.patch(`/batches/${batchId}/unfreeze`);

    // Track batch freeze/unfreeze (API toggles status)
    trackEvent('batch_status_toggled', {
      batch_id: batchId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteBatch = async batchId => {
  try {
    const response = await api.delete(`/batches/${batchId}`);

    // Track batch deletion
    trackEvent('batch_deleted', {
      batch_id: batchId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ── Draft & Reuse Batch API ────────────────────────────────────────────────

/**
 * publishBatch — transitions a draft batch to active.
 * Creates Stripe product + price on the backend at this point.
 */
export const publishBatch = async batchId => {
  try {
    const response = await api.post(`/batches/${batchId}/publish`);
    trackEvent('batch_published', { batch_id: batchId });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      // 402 = Stripe onboarding not done; surface this to the UI
      requiresOnboarding: error.response?.status === 402,
    };
  }
};

/**
 * reuseBatch — clones an existing batch as a new draft.
 * @param {number} sourceBatchId  The batch to clone from.
 * @param {{ name: string, start_date: string, end_date: string }} data
 */
export const reuseBatch = async (sourceBatchId, data) => {
  try {
    const response = await api.post(`/batches/${sourceBatchId}/reuse`, data);
    trackEvent('batch_reused', {
      source_batch_id: sourceBatchId,
      new_batch_name: data.name,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * getBatchTemplates — fetch smart pre-configured batch templates.
 */
export const getBatchTemplates = async () => {
  try {
    const response = await api.get('/batches/templates');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * rolloverBatch — clone batch for next term/month with priority re-enrollment.
 * @param {number} batchId
 * @param {{ new_name: string, start_date: string, end_date: string, price?: number, price_unit?: string, priority_window_days?: number, auto_notify_trainees?: boolean }} data
 */
export const rolloverBatch = async (batchId, data) => {
  try {
    const response = await api.post(`/batches/${batchId}/rollover`, data);
    trackEvent('batch_rolled_over', { batch_id: batchId, new_name: data.new_name });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * broadcastBatchMessage — send urgent broadcast announcement to batch trainees.
 * @param {number} batchId
 * @param {{ title: string, message: string, category?: string, send_push?: boolean, send_sms?: boolean }} data
 */
export const broadcastBatchMessage = async (batchId, data) => {
  try {
    const response = await api.post(`/batches/${batchId}/broadcast`, data);
    trackEvent('batch_broadcast_sent', { batch_id: batchId, category: data.category });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * openWhatsAppBroadcast — Client-Side 1-Tap Deep Link (Option A: Zero API Cost).
 * Opens WhatsApp Web/App pre-filled with the announcement message for 1-tap broadcast.
 * @param {string} messageText
 * @param {string[]} [phoneNumbers]
 */
export const openWhatsAppBroadcast = (messageText, phoneNumbers = []) => {
  const encodedText = encodeURIComponent(messageText);
  if (phoneNumbers && phoneNumbers.length === 1 && phoneNumbers[0]) {
    const cleanPhone = phoneNumbers[0].replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanPhone}?text=${encodedText}`, '_blank');
  } else {
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
  }
};

/**
 * getBatchFinancials — fetch real-time financial break-even & take-home breakdown for a batch.
 * @param {number} batchId
 */
export const getBatchFinancials = async batchId => {
  try {
    const response = await api.get(`/batches/${batchId}/financials`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * assignSubstituteCoach — assign a temporary substitute coach for a specific session date.
 * @param {number} batchId
 * @param {{ substitute_coach_id: number, date: string, reason?: string }} data
 */
export const assignSubstituteCoach = async (batchId, data) => {
  try {
    const response = await api.post(`/batches/${batchId}/substitute`, data);
    trackEvent('substitute_coach_assigned', { batch_id: batchId, date: data.date });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * getBatchSubstitutes — fetch substitute coach assignments for a batch.
 * @param {number} batchId
 */
export const getBatchSubstitutes = async (batchId) => {
  try {
    const response = await api.get(`/batches/${batchId}/substitutes`);
    return { success: true, data: response.data?.data || [] };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * getBatchesByStatus — fetch vendor batches filtered by lifecycle status.
 * @param {string} status  "draft" | "active" | "frozen" | "completed" | "" (all)
 */
export const getBatchesByStatus = async (status = '') => {
  try {
    const params = status ? { status } : {};
    const response = await api.get('/batches', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getBatchTrainees = async batchId => {
  try {
    const response = await api.get(`/batches/${batchId}/trainees`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getBatchHourLogs = async (batchId, params = {}) => {
  try {
    const response = await api.get(`/batches/${batchId}/hour-logs`, { params });
    const logData = response.data?.data || response.data;
    return { success: true, data: logData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getTraineeHourLogs = async (batchId, traineeId, params = {}) => {
  try {
    const response = await api.get(`/batches/${batchId}/trainees/${traineeId}/hour-logs`, { params });
    const logData = response.data?.data || response.data;
    return { success: true, data: logData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


// Sponsorships Management
export const createSponsorship = async sponsorshipData => {
  try {
    const response = await api.post('/sponsorships', sponsorshipData);

    // Track sponsorship creation
    trackEvent('sponsorship_created', {
      customer_id: sponsorshipData.customer_id,
      goal_amount: sponsorshipData.goal_amount,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getSponsorships = async () => {
  try {
    const response = await api.get('/sponsorships');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateSponsorshipStatus = async (id, status) => {
  try {
    const response = await api.patch(`/sponsorships/${id}/status`, { status });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


export const assignAttendancePoints = async (batchId, data) => {
  try {
    const response = await api.post(`/batches/${batchId}/attendance-points`, data);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getBatchAttendanceHistory = async (batchId, date) => {
  try {
    const params = {};
    if (date) params.date = date; // YYYY-MM-DD
    const response = await api.get(`/batches/${batchId}/attendance`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Trainees Management
// Get all trainees for the vendor
export const getTrainees = async (params = {}) => {
  try {
    const response = await api.get('/trainees', { params });
    return { success: true, data: response.data.data || response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Record a match result between two trainees
export const recordMatchResult = async (matchData) => {
  try {
    // Expected fields: player1_id, player2_id, player1_child_id, player2_child_id, winner_id, winner_child_id, score, sport_id
    const response = await api.post('/matches', matchData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Staff Management
export const getStaff = async () => {
  try {
    const response = await api.get('/staff');
    const responseData = response.data;

    // Transform the API response to match frontend expectations
    if (responseData.success && responseData.staff && Array.isArray(responseData.staff)) {
      const transformedStaff = responseData.staff.map(staff => {
        // Standard list of days
        const baseDays = [
          { id: 1, day: 'Monday', isSelected: false },
          { id: 2, day: 'Tuesday', isSelected: false },
          { id: 3, day: 'Wednesday', isSelected: false },
          { id: 4, day: 'Thursday', isSelected: false },
          { id: 5, day: 'Friday', isSelected: false },
          { id: 6, day: 'Saturday', isSelected: false },
          { id: 7, day: 'Sunday', isSelected: false },
        ];

        let trainingDays = [...baseDays];

        if (staff.training_days && Array.isArray(staff.training_days)) {
          // Check if it's already a clean array of strings (the new format)
          if (staff.training_days.length > 0 && typeof staff.training_days[0] === 'string' && !staff.training_days[0].trim().startsWith('{')) {
            const apiDays = staff.training_days;
            trainingDays = baseDays.map(d => ({
              ...d,
              isSelected: apiDays.includes(d.day)
            }));
          } else {
            try {
              // Handle malformed JSON legacy format
              let joinedStr = staff.training_days.join('');
              joinedStr = joinedStr.trim();
              if (joinedStr.startsWith('[')) joinedStr = joinedStr.substring(1);
              if (joinedStr.endsWith(']')) joinedStr = joinedStr.substring(0, joinedStr.length - 1);

              joinedStr = joinedStr.replace(/"\s*"([a-zA-Z_])/g, '","$1');
              joinedStr = joinedStr.replace(/\}\s*"([a-zA-Z_])/g, '},"$1');
              joinedStr = joinedStr.replace(/(\d)\s*"([a-zA-Z_])/g, '$1,"$2');
              joinedStr = joinedStr.replace(/(true|false)\s*"([a-zA-Z_])/g, '$1,"$2');
              joinedStr = joinedStr.replace(/\}\s*\{/g, '},{');

              const jsonArrayStr = '[' + joinedStr + ']';
              const parsed = JSON.parse(jsonArrayStr);

              if (Array.isArray(parsed)) {
                trainingDays = baseDays.map(baseDay => {
                  const found = parsed.find(d => d.day === baseDay.day);
                  return found ? { ...baseDay, isSelected: !!found.isSelected } : baseDay;
                });
              }
            } catch (e) {
              console.warn('Failed to parse training_days:', e, staff.training_days);
            }
          }
        }

        const avatarUrl = staff.profile_pic
          ? `${staff.profile_pic}`
          : null;

        return {
          id: staff.id,
          uid: staff.id, // For compatibility
          vendor_id: staff.vendor_id,
          is_active: staff.is_active,
          // Name fields
          first_name: staff.first_name,
          last_name: staff.last_name,
          Fname: staff.first_name,
          Lname: staff.last_name,
          name: `${staff.first_name || ''} ${staff.last_name || ''}`.trim(),
          // Services
          services: staff.services || [],
          // Time fields
          starting_time: staff.starting_time,
          ending_time: staff.ending_time,
          startingTime: staff.starting_time,
          endingTime: staff.ending_time,
          // Image
          profile_pic: staff.profile_pic,
          avatar: avatarUrl,
          // Cost
          cost: staff.cost || 0,
          price_unit: staff.price_unit || staff.pricing_unit || 'One time',
          // Training days
          training_days: trainingDays,
          trainingDays: trainingDays,
          // Dates
          created_at: staff.created_at,
          createdAt: staff.created_at,
          updated_at: staff.updated_at,
          updatedAt: staff.updated_at,
          // Default values for fields that might not exist
          contractType: staff.contract_type || null,
          hoursWorked: staff.hours_worked || 0,
          chargesPerHour: staff.charges_per_hour || staff.cost || 0,
          monthlyHours: staff.monthly_hours || 0,
          leaveSchedule: staff.leave_schedule || null,
          weekly_hours: staff.weekly_hours || 0,
          role: staff.role || 'Trainer',
        };
      });

      return {
        success: true,
        data: transformedStaff,
        pagination: responseData.pagination,
        vendor_id: responseData.vendor_id,
      };
    }

    // Fallback for unexpected response structure
    return { success: true, data: responseData.staff || responseData.data || [] };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getStaffById = async staffId => {
  try {
    const response = await api.get(`/staff/${staffId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getStaffByIds = async staffIds => {
  try {
    const response = await api.post('/staff/batch', { ids: staffIds });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};
export const createStaff = async staffData => {
  try {
    const formData = new FormData();

    // Mapping fields to backend expectations
    const first_name = staffData.first_name || staffData.Fname;
    const last_name = staffData.last_name || staffData.Lname;
    const training_days = staffData.training_days || staffData.trainingDays;
    const profile_pic = staffData.profile_pic || staffData.avatar;
    const email = staffData.email
    const password = staffData.password
    const description = staffData.description
    const weekly_hours = staffData.weekly_hours
    const role = staffData.role
    const price_unit = staffData.price_unit

    if (first_name) formData.append('first_name', first_name);
    if (last_name) formData.append('last_name', last_name);
    if (staffData.name) formData.append('name', staffData.name);
    if (staffData.cost !== undefined) formData.append('cost', staffData.cost);
    if (weekly_hours !== undefined) formData.append('weekly_hours', weekly_hours);
    if (price_unit !== undefined) formData.append('price_unit', price_unit);

    if (staffData.services && Array.isArray(staffData.services)) {
      staffData.services.forEach(service => {
        formData.append('services', service);
      });
    }

    if (training_days && Array.isArray(training_days)) {
      training_days.forEach(day => {
        if (typeof day === 'string') {
          formData.append('training_days', day);
        } else if (day && day.isSelected) {
          formData.append('training_days', day.day);
        }
      });
    }

    if (profile_pic && profile_pic instanceof File) {
      formData.append('profile_pic', profile_pic);
    }

    if (email) formData.append('email', email);
    if (password) formData.append('password', password);

    if (description) formData.append('description', description)
    if (role) formData.append('role', role);

    // Use a separate axios instance for multipart/form-data
    const response = await axios.post(`${BASE_URL}/staff`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    // Track staff creation
    trackEvent('staff_created', {
      has_email: !!email,
      services_count: staffData.services?.length || 0,
      has_profile_pic: !!(profile_pic && profile_pic instanceof File),
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateStaff = async (staffId, staffData) => {
  try {
    const formData = new FormData();

    // Mapping fields to backend expectations
    const first_name = staffData.first_name || staffData.Fname;
    const last_name = staffData.last_name || staffData.Lname;
    const training_days = staffData.training_days || staffData.trainingDays;
    const profile_pic = staffData.profile_pic || staffData.avatar;

    const description = staffData.description;
    const weekly_hours = staffData.weekly_hours;
    const role = staffData.role;
    const price_unit = staffData.price_unit

    if (first_name) formData.append('first_name', first_name);
    if (last_name) formData.append('last_name', last_name);
    if (staffData.name) formData.append('name', staffData.name);
    if (staffData.cost !== undefined) formData.append('cost', staffData.cost);
    if (weekly_hours !== undefined) formData.append('weekly_hours', weekly_hours);
    if (description) formData.append('description', description);
    if (role) formData.append('role', role);
    if (price_unit) formData.append('price_unit', price_unit)

    if (staffData.services && Array.isArray(staffData.services)) {
      staffData.services.forEach(service => {
        formData.append('services', service);
      });
    }

    if (training_days && Array.isArray(training_days)) {
      training_days.forEach(day => {
        if (typeof day === 'string') {
          formData.append('training_days', day);
        } else if (day && day.isSelected) {
          formData.append('training_days', day.day);
        }
      });
    }

    if (profile_pic && profile_pic instanceof File) {
      formData.append('profile_pic', profile_pic);
    }

    // Use a separate axios instance for multipart/form-data
    const response = await axios.put(`${BASE_URL}/staff/${staffId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    // Track staff update
    trackEvent('staff_updated', {
      staff_id: staffId,
      has_profile_pic: !!(profile_pic && profile_pic instanceof File),
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteStaff = async staffId => {
  try {
    const response = await api.delete(`/staff/${staffId}`);

    // Track staff deletion
    trackEvent('staff_deleted', {
      staff_id: staffId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getStaffFeedback = async staffId => {
  try {
    const response = await api.get(`/staff/${staffId}/feedback`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getStaffBatches = async staffId => {
  try {
    const response = await api.get(`/staff/${staffId}/batches`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getStaffPaymentInfo = async staffId => {
  try {
    const response = await api.get(`/staff/${staffId}/payment-info`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const addCoachFeedback = async (customerId, feedbackData) => {
  try {
    const response = await api.post(`/coach/feedback/${customerId}`, {
      coach_id: feedbackData.coach_id,
      batch_id: feedbackData.batch_id,
      rating: feedbackData.rating,
      feedback: feedbackData.feedback,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getCoachFeedbackToCustomer = async customerId => {
  try {
    const response = await api.get(`/coach/feedback/customer/${customerId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Services Management
export const getServices = async () => {
  try {
    const response = await api.get('/services');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getServiceById = async (serviceId) => {
  try {
    const response = await api.get(`/services/${serviceId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Service Boosting
export const getPromotionOptions = async (serviceId) => {
  try {
    const response = await api.get(`/services/${serviceId}/promotion-options`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const initiateBoosting = async (serviceId, duration) => {
  try {
    const response = await api.post(`/services/${serviceId}/boost?duration=${duration}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


export const getServiceWithBatches = async (serviceId = null, paginationParams = {}) => {
  try {
    const params = {};

    // Add pagination parameters if provided
    if (paginationParams.limit) params.limit = paginationParams.limit;
    if (paginationParams.page) params.page = paginationParams.page;

    // Build endpoint URL
    const endpoint = serviceId ? `/services/${serviceId}` : '/services';

    const response = await api.get(endpoint, { params });

    // Handle the response structure: { pagination, services, success, vendor_id }
    const responseData = response.data;

    // If response has services array (paginated response)
    if (responseData.services && Array.isArray(responseData.services)) {
      // If serviceId is provided, find and return the matching service
      if (serviceId) {
        const service = responseData.services.find(s => s.id === parseInt(serviceId));
        if (service) {
          return {
            success: true,
            data: service,
            pagination: responseData.pagination,
            vendor_id: responseData.vendor_id
          };
        }
      }
      // Return all services with pagination info
      return {
        success: true,
        data: responseData.services,
        pagination: responseData.pagination,
        vendor_id: responseData.vendor_id
      };
    }

    // If response is a single service object (non-paginated response)
    // or has a different structure, return as-is
    return {
      success: true,
      data: responseData,
      pagination: responseData.pagination,
      vendor_id: responseData.vendor_id
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createService = async serviceData => {
  try {
    // Create FormData for service creation (supports file uploads)
    const formData = new FormData();

    // Only append: name, cover, description
    if (serviceData.name) {
      formData.append('service_name', serviceData.name);
    }

    if (serviceData.cover && serviceData.cover instanceof File) {
      formData.append('cover', serviceData.cover);
    }

    if (serviceData.description) {
      formData.append('description', serviceData.description);
    }

    if (serviceData.start_timing) {
      formData.append('start_timing', serviceData.start_timing);
    }

    if (serviceData.end_timing) {
      formData.append('end_timing', serviceData.end_timing);
    }

    if (serviceData.service_subtitle) {
      formData.append('service_subtitle', serviceData.service_subtitle);
    }

    // Debug: Log FormData contents
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[1] instanceof File ? `[File: ${pair[1].name}]` : pair[1]));
    }

    const response = await axios.post(`${BASE_URL}/services`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    // Track service creation
    trackEvent('service_created', {
      service_name: serviceData.name,
      has_cover: !!serviceData.cover,
      has_description: !!serviceData.description,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateService = async (serviceId, serviceData) => {
  try {
    // Check if serviceData is FormData (for file uploads)
    if (serviceData instanceof FormData) {
      // Use a separate axios instance for multipart/form-data
      const response = await axios.put(`${BASE_URL}/services/${serviceId}`, serviceData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Track service update
      trackEvent('service_updated', {
        service_id: serviceId,
        update_type: 'with_files',
      });

      return { success: true, data: response.data };
    } else {
      // Use regular API instance for JSON data
      const response = await api.put(`/services/${serviceId}`, serviceData);

      // Track service update
      trackEvent('service_updated', {
        service_id: serviceId,
        update_type: 'json',
      });

      return { success: true, data: response.data };
    }
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteService = async serviceId => {
  try {
    const response = await api.delete(`/services/${serviceId}`);

    // Track service deletion
    trackEvent('service_deleted', {
      service_id: serviceId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Profile Management
export const getProfile = async () => {
  try {
    const response = await api.get('/profile');
    // Handle different response structures
    // If response.data has a nested data property, use that
    // Otherwise use response.data directly
    let profileData = response.data?.data || response.data;
    if (Array.isArray(profileData) && profileData.length === 1) {
      profileData = profileData[0];
    }
    return { success: true, data: profileData };
  } catch (error) {
    console.error('Get profile error:', error);
    console.error('Error response:', error.response);
    return {
      success: false,
      error: error.response?.data?.error || error.response?.data?.message || error.message,
    };
  }
};

export const updateProfile = async profileData => {
  try {
    // Create FormData for multipart/form-data
    const formData = new FormData();

    // Append form fields
    if (profileData.name) formData.append('name', profileData.name);
    if (profileData.username) formData.append('username', profileData.username);
    if (profileData.country) formData.append('country', profileData.country);
    if (profileData.city !== undefined && profileData.city !== null) formData.append('city', profileData.city);
    if (profileData.county !== undefined && profileData.county !== null) formData.append('county', profileData.county);
    if (profileData.address) formData.append('address', profileData.address);
    if (profileData.postcode) formData.append('postcode', profileData.postcode);
    if (profileData.starting_time) formData.append('starting_time', profileData.starting_time);
    if (profileData.ending_time) formData.append('ending_time', profileData.ending_time);
    if (profileData.latitude) formData.append('latitude', profileData.latitude);
    if (profileData.longitude) formData.append('longitude', profileData.longitude);

    if (profileData.description !== undefined && profileData.description !== null) {
      formData.append('description', profileData.description);
    }
    // Append training days if provided (as JSON string)
    // if (profileData.training_days && Array.isArray(profileData.training_days)) {
    //   formData.append('training_days', JSON.stringify(profileData.training_days));
    // }
    if (profileData.training_days && Array.isArray(profileData.training_days)) {
      profileData.training_days.forEach(day => {
        if (typeof day === 'string') {
          formData.append('training_days', day);
        } else if (day && day.isSelected) {
          formData.append('training_days', day.day);
        }
      });
    }

    // Append file if provided (handle FileList or File object)
    if (profileData.logo) {
      if (profileData.logo instanceof File) {
        formData.append('logo', profileData.logo);
      } else if (profileData.logo instanceof FileList && profileData.logo.length > 0) {
        formData.append('logo', profileData.logo[0]);
      }
    }
    if (profileData.business_registration_number) {
      formData.append('business_registration_number', profileData.business_registration_number);
    }
    if (profileData.business_legal_name) {
      formData.append('business_legal_name', profileData.business_legal_name);
    }
    if (profileData.is_teaching_children) {
      formData.append('dbs', profileData.is_teaching_children);
    }
    if (profileData.dbs_certificate) {
      if (profileData.dbs_certificate instanceof File) {
        formData.append('dbs_certificate', profileData.dbs_certificate);
      } else if (profileData.dbs_certificate instanceof FileList && profileData.dbs_certificate.length > 0) {
        formData.append('dbs_certificate', profileData.dbs_certificate[0]);
      }
    }

    // Use a separate axios instance for multipart/form-data
    // Note: URL with trailing slash as specified
    const response = await api.put(`/profile`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Town Halls Management (Handled by aliases below)

// Operations Management
export const getOperations = async () => {
  try {
    const response = await api.get('/operations');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Events Operations
export const getEvents = async () => {
  try {
    const response = await api.get('/operations/events');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createEvent = async eventData => {
  try {
    const response = await api.post('/operations/events', eventData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateEvent = async (eventId, eventData) => {
  try {
    const response = await api.put(`/operations/events/${eventId}`, eventData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteEvent = async eventId => {
  try {
    const response = await api.delete(`/operations/events/${eventId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Expenses Operations
export const getExpenses = async () => {
  try {
    const response = await api.get('/operations/expenses');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createExpense = async expenseData => {
  try {
    const response = await api.post('/operations/expenses', expenseData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateExpense = async (expenseId, expenseData) => {
  try {
    const response = await api.put(`/operations/expenses/${expenseId}`, expenseData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteExpense = async expenseId => {
  try {
    const response = await api.delete(`/operations/expenses/${expenseId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Bulk Update Expenses (for operations page)
export const updateExpensesBulk = async expenses => {
  try {
    const response = await api.put('/operations/expenses/bulk', { expenses });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Holidays Operations
export const getHolidays = async () => {
  try {
    const response = await api.get('/operations/holidays');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createHoliday = async holidayData => {
  try {
    const response = await api.post('/operations/holidays', holidayData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateHoliday = async (holidayId, holidayData) => {
  try {
    const response = await api.put(`/operations/holidays/${holidayId}`, holidayData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteHoliday = async holidayId => {
  try {
    const response = await api.delete(`/operations/holidays/${holidayId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Profile Note Operations
export const updateProfileNote = async (noteId, noteData) => {
  try {
    const response = await api.put(`/operations/profile/notes/${noteId}`, noteData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Profile Details Operations
export const updateProfileDetails = async (detailsId, details) => {
  try {
    const response = await api.put(`/operations/edit/${detailsId}`, details);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Training Halls Operations
export const getTrainingHalls = async () => {
  try {
    const response = await api.get('/townhall');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createTrainingHall = async hallData => {
  try {
    const response = await api.post('/townhall', hallData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateTrainingHall = async (hallId, hallData) => {
  try {
    const response = await api.put(`/townhall/${hallId}`, hallData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteTrainingHall = async hallId => {
  try {
    const response = await api.delete(`/townhall/${hallId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getHallAvailability = async (hallId, params) => {
  try {
    const response = await api.get(`/townhall/${hallId}/availability`, { params });
    return response.data;
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Aliases for backward compatibility
export const getTownHalls = getTrainingHalls;
export const createTownHall = createTrainingHall;
export const updateTownHall = updateTrainingHall;
export const deleteTownHall = deleteTrainingHall;

// Profile Notes Operations (for backward compatibility)
export const getProfileNotes = async () => {
  try {
    const response = await api.get('/operations/profile/notes');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createProfileNote = async noteData => {
  try {
    const response = await api.post('/operations/profile/notes', noteData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteProfileNote = async noteId => {
  try {
    const response = await api.delete(`/operations/profile/notes/${noteId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Edit Operations
export const getEditOperations = async () => {
  try {
    const response = await api.get('/operations/edit');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createEditOperation = async operationData => {
  try {
    const response = await api.post('/operations/edit', operationData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateEditOperation = async (operationId, operationData) => {
  try {
    const response = await api.put(`/operations/edit/${operationId}`, operationData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteEditOperation = async operationId => {
  try {
    const response = await api.delete(`/operations/edit/${operationId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ==================== Sales Management Endpoints ====================

// Get total subscribers count
// Note: Backend has typo "subsciber" instead of "subscriber"
export const getTotalSubscribersCount = async () => {
  try {
    const response = await api.get('/sales/subsciber/count');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get all subscriptions with optional filters
export const getSubscriptions = async (params = {}) => {
  try {
    const response = await api.get('/sales/subscriptions', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ==================== Trainee Subscription Management ====================

// Get trainee subscriptions (detailed list with payment info)
export const getTraineeSubscriptions = async (params = {}) => {
  try {
    const response = await api.get('/trainees/subscriptions', { params });
    // Expected response: { success: true, subscriptions: [...] }
    const subscriptionData = response.data?.subscriptions || response.data?.data || [];
    return { success: true, data: subscriptionData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get trainee history (progress logs and summary)
export const getTraineeHistory = async (batchId, customerId, childId) => {
  try {
    let url = `/batches/${batchId}/trainees/${customerId}/history`;
    if (childId) {
      url += `?child_id=${childId}`;
    }
    const response = await api.get(url);
    const historyData = response.data?.data || response.data;
    return { success: true, data: historyData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Freeze or unfreeze trainee subscription
export const toggleSubscriptionFreeze = async (subscriptionId, shouldFreeze) => {
  try {
    // Determine endpoint based on action
    const action = shouldFreeze ? 'freeze' : 'unfreeze';
    const response = await api.post(`/trainees/subscriptions/${subscriptionId}/${action}`);

    // Track subscription freeze/unfreeze
    trackEvent(shouldFreeze ? 'subscription_frozen' : 'subscription_unfrozen', {
      subscription_id: subscriptionId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Send payment reminder
export const sendPaymentReminder = async (subscriptionId) => {
  try {
    const response = await api.post(`/trainees/subscriptions/${subscriptionId}/remind`);

    // Track payment reminder sent
    trackEvent('payment_reminder_sent', {
      subscription_id: subscriptionId,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Cancel trainee subscription
export const cancelTraineeSubscription = async (subscriptionId, cancelAtPeriodEnd = false) => {
  try {
    const response = await api.delete(`/trainees/subscriptions/${subscriptionId}/cancel`, {
      data: { cancel_at_period_end: cancelAtPeriodEnd }
    });

    // Track subscription cancellation
    trackEvent('subscription_cancelled', {
      subscription_id: subscriptionId,
      cancel_at_period_end: cancelAtPeriodEnd,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get custom offers count
export const getCustomOffersCount = async (params = {}) => {
  try {
    const response = await api.get('/sales/offers/count', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get all custom offers (bids)
export const getAllOffers = async (params = {}) => {
  try {
    const response = await api.get('/sales/offers',);
    const profileData = response.data?.data || response.data;
    return { success: true, data: profileData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ==================== Refund Management ====================

export const getRefunds = async () => {
  try {
    const response = await api.get('/refunds');
    // Handle wrapped response structure { data: [...], success: true }
    const refundsData = response.data?.data || response.data;
    return { success: true, data: refundsData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get disputes
export const getDisputes = async () => {
  try {
    const response = await api.get('/disputes');
    // Handle wrapped response structure { data: [...], success: true }
    const disputesData = response.data?.data || response.data;
    return { success: true, data: disputesData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const processRefund = async (refundId, status, refundResponse) => {
  try {
    // POST refunds with refund_id, response, status (Rejected, Approved)
    const payload = {
      refund_id: refundId,
      status: status,
      response: refundResponse
    };

    const response = await api.post('/refunds', payload);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Update custom offer (bid) status
// Note: Backend route has double slash, but axios will normalize it
export const updateOffer = async (offerId, offerData) => {
  try {
    const response = await api.patch(`/sales/offers/${offerId}`, offerData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get expiring subscriptions
export const getExpiringSubscriptions = async (params = {}) => {
  try {
    const response = await api.get('/sales/subscriptions/expiring', { params });
    // Backend returns { success: true, data: [...] }, so return it directly
    return response.data;
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get subscriptions chart data
export const getSubscriptionsChartData = async (params = {}) => {
  try {
    const response = await api.get('/sales/subscriptions/chart', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ==================== Dashboard Analytics Endpoints ====================

// Get dashboard analytics data (subscribers, revenue, cost with time series)
export const getDashboardAnalytics = async (params = {}) => {
  try {
    const response = await api.get('/dashboard/analytics', { params });
    // Backend returns { success: true, data: {...} }, so return it directly
    return response.data;
  } catch (error) {
    console.error('vendorAPI: Dashboard analytics error:', error);
    console.error('vendorAPI: Error response:', error.response);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ==================== Finance Management Endpoints ====================

// Get finance analytics data
export const getFinanceAnalytics = async (params = {}) => {
  try {
    const response = await api.get('/finance/analytics', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get finance summary (outstanding owed & payout)
export const getFinanceSummary = async (params = {}) => {
  try {
    const response = await api.get('/finance/summary', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get payout history (paginated, status-filterable)
export const getPayoutHistory = async (params = {}) => {
  try {
    const response = await api.get('/finance/payouts/history', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get upcoming (scheduled) payouts
export const getUpcomingPayouts = async (params = {}) => {
  try {
    const response = await api.get('/finance/payouts/upcoming', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get vendor payment information
export const getVendorPaymentInfo = async (params = {}) => {
  try {
    const response = await api.get('/finance/payment-info', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Update vendor payment information
export const updateVendorPaymentInfo = async (paymentInfoData) => {
  try {
    const response = await api.put('/finance/payment-info', paymentInfoData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getCountries = async () => {
  try {
    // For Static List of Countries: 
    console.log('vendorAPI: Getting countries');
    const response = {
      success: true,
      data: [
        {
          id: 1,
          name: 'United Kingdom',
          code: 'UK',
          currency: 'GBP',
          icon: 'https://flagcdn.com/gb.svg',
        },
        {
          id: 2,
          name: 'United States',
          code: 'US',
          currency: 'USD',
          icon: 'https://flagcdn.com/us.svg',
        },
        {
          id: 3,
          name: 'Canada',
          code: 'CA',
          currency: 'CAD',
          icon: 'https://flagcdn.com/ca.svg',
        },
      ],
    };

    return response.data;
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Push Notifications
export const registerPushToken = async (token, platform = 'web') => {
  try {
    const response = await api.post(`${BASE_API}/api/v1/notifications/tokens`, {
      token,
      platform,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const unregisterPushToken = async (token) => {
  try {
    const response = await api.delete(`${BASE_API}/api/v1/notifications/tokens`, {
      data: { token },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Coupon Management
export const getCoupons = async () => {
  try {
    const response = await api.get('/coupons');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createCoupon = async (couponData) => {
  try {
    const response = await api.post('/coupons', couponData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deactivateCoupon = async (id) => {
  try {
    const response = await api.delete(`/coupons/${id}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Promotion Management
export const getPromotions = async () => {
  try {
    const response = await api.get('/promotions');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createPromotion = async (promotionData) => {
  try {
    const response = await api.post('/promotions', promotionData);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deactivatePromotion = async (id) => {
  try {
    const response = await api.delete(`/promotions/${id}`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Stripe Connected Account Onboarding
export const initiateStripeOnboarding = async () => {
  try {
    const response = await api.post('/stripe/onboard');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Get Stripe Connected Account status
export const getStripeOnboardStatus = async () => {
  try {
    const response = await api.get('/stripe/onboard/status');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Club Analytics (AI Business Consultant)
export const getClubAnalyticsReport = async () => {
  try {
    const response = await api.get('/analytics');
    const payload = response.data?.data ?? response.data;
    const report =
      payload?.club_analytics ??
      payload?.clubAnalyticsReport ??
      payload?.weekly_report ??
      payload;

    if (report?.summary) {
      return { success: true, data: report };
    }
  } catch (error) {
    // Fall through to trend-reports
  }

  try {
    const trendRes = await api.get('/trend-reports/latest');
    const payload = trendRes.data?.data ?? trendRes.data;
    const report = payload?.report ?? payload?.club_analytics ?? payload;

    if (report?.summary) {
      return { success: true, data: report };
    }

    const listRes = await api.get('/trend-reports', { params: { page: 1, limit: 1 } });
    const listPayload = listRes.data?.data ?? listRes.data;
    const reports = listPayload?.reports ?? listPayload;
    const latest = Array.isArray(reports) ? reports[0] : null;

    if (latest?.summary) {
      return { success: true, data: latest };
    }

    return { success: false, error: 'No club analytics report available' };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// Skill Evaluation Rubrics
export const getSkillRubrics = async (sportId) => {
  try {
    const response = await api.get('/evaluations/rubrics', {
      params: sportId ? { sport_id: sportId } : {},
    });
    const data = response.data?.data ?? response.data;
    return {
      success: true,
      data: Array.isArray(data) ? data : (data?.rubrics ?? []),
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const pollSkillRubrics = async (sportId, { maxAttempts = 12, intervalMs = 3000 } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const result = await getSkillRubrics(sportId);
    if (result.success && result.data?.length > 0) {
      return result;
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }
  return { success: true, data: [], provisioning: true };
};

export const updateSkillRubric = async (rubricId, payload) => {
  try {
    const response = await api.put(`/evaluations/rubrics/${rubricId}`, payload);
    return {
      success: true,
      data: response.data?.data ?? response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const addRubricCategory = async (rubricId, payload) => {
  try {
    const response = await api.post(`/evaluations/rubrics/${rubricId}/categories`, payload);
    return {
      success: true,
      data: response.data?.data ?? response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const updateRubricCategory = async (rubricId, categoryId, payload) => {
  try {
    const response = await api.put(
      `/evaluations/rubrics/${rubricId}/categories/${categoryId}`,
      payload,
    );
    return {
      success: true,
      data: response.data?.data ?? response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const deleteRubricCategory = async (rubricId, categoryId) => {
  try {
    const response = await api.delete(
      `/evaluations/rubrics/${rubricId}/categories/${categoryId}`,
    );
    return {
      success: true,
      data: response.data,
      status: response.status,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const submitSkillEvaluation = async (payload) => {
  try {
    const response = await api.post('/evaluations', payload);
    return {
      success: true,
      data: response.data?.data ?? response.data,
      message: response.data?.message,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      error: error.response?.data?.error || error.message,
      details: error.response?.data?.details,
    };
  }
};

export default api;


// Trend Reports
export const getLatestTrendReport = async () => {
  try {
    const response = await api.get('/trend-reports/latest');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getTrendReports = async (page = 1, limit = 10) => {
  try {
    const response = await api.get('/trend-reports', { params: { page, limit } });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const generateTrendReport = async () => {
  try {
    const response = await api.post('/trend-reports/generate');

    // Track report generation
    trackEvent('trend_report_generated');

    return { success: true, data: response.data };
  } catch (error) {
    // Note: status 429 means too many requests
    return {
      success: false,
      status: error.response?.status,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getTrendReportSnapshots = async (reportId) => {
  try {
    const params = reportId ? { report_id: reportId } : {};
    const response = await api.get('/trend-reports/snapshots', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getEvaluationHistory = async (batchId) => {
  try {
    const response = await api.get('/evaluations/history', {
      params: { batch_id: batchId },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ==================== Challenges ====================

export const getVendorChallengeServices = async () => {
  try {
    const response = await api.get('/challenges/services');
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getEligiblePlayers = async (sportId) => {
  try {
    const response = await api.get('/challenges/eligible-players', {
      params: { sport_id: sportId },
    });
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getChallenges = async () => {
  try {
    const response = await api.get('/challenges');
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const createChallenge = async (payload) => {
  try {
    const response = await api.post('/challenges', payload);
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const submitChallengeResult = async (challengeId, winningTeam) => {
  try {
    const response = await api.post(`/challenges/${challengeId}/result`, {
      winning_team: winningTeam,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


export const reassignTraineeBatch = async (subscriptionId, newBatchId) => {
  try {
    const response = await api.put(`/trainees/subscriptions/${subscriptionId}/reassign`, {
      new_batch_id: newBatchId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

export const getTraineeTransactionHistory = async (subscriptionId) => {
  try {
    const response = await api.get(`/trainees/subscriptions/${subscriptionId}/history`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

export const getServiceSavesAnalytics = async (serviceId) => {
  try {
    const response = await api.get(`/services/${serviceId}/analytics`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};



/**
 * getBatchWaitlist — fetch waitlisted trainees for a batch.
 * @param {number} batchId
 */
export const getBatchWaitlist = async batchId => {
  try {
    const response = await api.get(`/batches/${batchId}/waitlist`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * getCheckinQRCode — fetch 60-sec rolling QR code for coach screen kiosk check-in.
 * @param {number} batchId
 */
export const getCheckinQRCode = async batchId => {
  try {
    const response = await api.get(`/batches/${batchId}/checkin-qr`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


/**
 * jumpStartOnboarding — 1-step guided setup wizard for older partners.
 * Creates both Service and Batch in a single guided API call.
 * @param {{ sport_icon_id: number, session_name: string, days: string[], start_time: string, end_time: string, price: number, currency?: string, capacity: number, hall_id?: number, profile_image?: string, service_cover_image?: string, trainer_image?: string, save_as_draft?: boolean }} data
 */
export const jumpStartOnboarding = async data => {
  try {
    const response = await api.post('/onboarding/jumpstart', data);
    trackEvent('jumpstart_onboarding_completed', { sport_icon_id: data.sport_icon_id });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * aiJumpStartOnboarding — AI-powered 1-step setup with full image support.
 * Creates Vendor Profile + Hall + Head Coach + Service + Batch atomically.
 */
export const aiJumpStartOnboarding = async data => {
  try {
    const response = await api.post('/onboarding/ai-jumpstart', data);
    trackEvent('ai_jumpstart_onboarding_completed', { sport_icon_id: data.sport_icon_id });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * aiSuggestOnboarding — Returns smart defaults based on sport category.
 * Zero LLM cost — pure lookup table on backend.
 * @param {{ sport_category: string, academy_name?: string }} data
 */
export const aiSuggestOnboarding = async data => {
  try {
    const response = await api.post('/onboarding/ai-suggest', data);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * aiExtractOnboarding — Extracts structured venue details from a Facebook page, sports website URL,
 * or timetable photo/PDF flyer using server-side AI and crawler emulation.
 * @param {{ url?: string, file_base64?: string, mime_type?: string } | FormData} data
 */
export const aiExtractOnboarding = async data => {
  try {
    let response;
    if (data instanceof FormData) {
      response = await api.post('/onboarding/ai-extract', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      response = await api.post('/onboarding/ai-extract', data);
    }
    return { success: true, data: response.data?.data, meta: response.data?.meta };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


/**
 * uploadOnboardingImage — Enqueues a base64 image for background upload to R2.
 * @param {{ entity_id: number, entity_type: string, image_type: string, base64_data: string, file_name?: string }} data
 */
export const uploadOnboardingImage = async data => {
  try {
    const response = await api.post('/onboarding/upload-image', data);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};


/**
 * getAvailableStaff — query available coaches with no batch conflict on day and time.
 * @param {string} day  e.g. "Monday"
 * @param {string} time e.g. "17:00"
 */
export const getAvailableStaff = async (day, time) => {
  try {
    const response = await api.get('/staff/availability', { params: { day, time } });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * getWeeklySchedule — returns all coaches and their batch slots for the weekly grid.
 */
export const getWeeklySchedule = async () => {
  try {
    const response = await api.get('/staff/weekly-schedule');
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};
